import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { FollowUpCard } from "@/components/FollowUpCard";
import { StatusBadge } from "@/components/StatusBadge";
import { fmtEUR, fmtDateTime, ACTIVITY_META } from "@/lib/constants";
import {
  Users, PhoneCall, Star, PhoneForwarded, FileText, Trophy, XCircle,
  Wallet, TrendingUp, CalendarClock, Loader2, Inbox,
} from "lucide-react";
import * as Icons from "lucide-react";
import { cn } from "@/lib/utils";

const KPIS = [
  { key: "all_leads", label: "Alle Leads", icon: Users, tint: "text-blue-400", to: "/leads" },
  { key: "today_todo", label: "Heute zu kontaktieren", icon: CalendarClock, tint: "text-amber-400", to: "/followups" },
  { key: "interested", label: "Interessiert", icon: Star, tint: "text-indigo-400", to: "/leads?status=interesse" },
  { key: "callbacks", label: "Rückrufe", icon: PhoneForwarded, tint: "text-cyan-400" },
  { key: "open_offers", label: "Angebote offen", icon: FileText, tint: "text-purple-400", to: "/leads?status=angebot" },
  { key: "customers", label: "Gewonnene Kunden", icon: Trophy, tint: "text-emerald-400", to: "/kunden" },
  { key: "lost", label: "Verlorene Leads", icon: XCircle, tint: "text-rose-400", to: "/leads?status=verloren" },
  { key: "potential_value", label: "Potenzieller Wert", icon: Wallet, tint: "text-sky-400", money: true },
  { key: "won_value", label: "Gewonnener Wert", icon: TrendingUp, tint: "text-emerald-400", money: true },
];

export default function Dashboard() {
  const navigate = useNavigate();
  const { data: stats, isLoading } = useQuery({ queryKey: ["stats"], queryFn: () => api.get("/dashboard/stats").then((r) => r.data) });
  const { data: today = [] } = useQuery({ queryKey: ["fu", "today"], queryFn: () => api.get("/followups?view=today").then((r) => r.data) });
  const { data: overdue = [] } = useQuery({ queryKey: ["fu", "overdue"], queryFn: () => api.get("/followups?view=overdue").then((r) => r.data) });
  const { data: recent = [] } = useQuery({ queryKey: ["recent"], queryFn: () => api.get("/activities/recent").then((r) => r.data) });

  const todo = [...overdue, ...today];

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 fade-in">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Dashboard</h1>
        <p className="text-sm text-muted-foreground mt-1">Dein Akquise-Überblick für heute.</p>
      </div>

      {/* KPI grid */}
      {isLoading ? (
        <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-5 gap-3 sm:gap-4">
          {KPIS.map((k) => (
            <button key={k.key} data-testid={`kpi-${k.key}`}
              onClick={() => k.to && navigate(k.to)}
              className={cn("group text-left rounded-xl border border-border bg-card p-4 transition-all",
                k.to ? "hover:border-primary/40 hover:-translate-y-0.5 cursor-pointer" : "cursor-default")}>
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground">{k.label}</span>
                <k.icon className={cn("h-4 w-4", k.tint)} />
              </div>
              <div className="mt-2 text-2xl font-bold tracking-tight">
                {k.money ? fmtEUR(stats?.[k.key]) : (stats?.[k.key] ?? 0)}
              </div>
            </button>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* Heute zu tun */}
        <div className="xl:col-span-2 space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <CalendarClock className="h-5 w-5 text-amber-400" /> Heute zu tun
            </h2>
            {overdue.length > 0 && (
              <span className="text-xs font-medium text-rose-300">{overdue.length} überfällig</span>
            )}
          </div>
          {todo.length === 0 ? (
            <div className="rounded-xl border border-dashed border-border p-8 text-center">
              <Inbox className="h-8 w-8 mx-auto text-muted-foreground/50" />
              <p className="mt-2 text-sm text-muted-foreground">Keine offenen Follow-ups für heute. Sauber!</p>
            </div>
          ) : (
            <div className="space-y-3">
              {todo.map((fu) => <FollowUpCard key={fu.id} fu={fu} />)}
            </div>
          )}
        </div>

        {/* Letzte Aktivitäten */}
        <div className="space-y-3">
          <h2 className="text-lg font-semibold">Letzte Aktivitäten</h2>
          <div className="rounded-xl border border-border bg-card p-4">
            {recent.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4 text-center">Noch keine Aktivitäten.</p>
            ) : (
              <ol className="space-y-4">
                {recent.map((a) => {
                  const meta = ACTIVITY_META[a.type] || ACTIVITY_META.notiz;
                  const Icon = Icons[meta.icon] || Icons.Circle;
                  return (
                    <li key={a.id} className="flex gap-3">
                      <div className={cn("mt-0.5 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-accent", meta.color)}>
                        <Icon className="h-3.5 w-3.5" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <button onClick={() => navigate(`/leads/${a.lead_id}`)}
                          className="text-sm font-medium hover:text-primary transition-colors">
                          {a.company}
                        </button>
                        <p className="text-xs text-muted-foreground break-words">{a.description}</p>
                        <p className="text-[11px] text-muted-foreground/70 mt-0.5">{fmtDateTime(a.created_at)}</p>
                      </div>
                    </li>
                  );
                })}
              </ol>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
